import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import HostCard from "@/components/HostCard";
import { Input } from "@/components/ui/input";
import { Search, SlidersHorizontal, MapPin } from "lucide-react";

const INTERESTS = ["Coffee", "Walks", "Board Games", "Books", "Music", "Art", "Food", "Fitness", "Movies", "Startups", "Photography", "Travel"];
const GENDERS = [{ v: "any", l: "Everyone" }, { v: "female", l: "Women" }, { v: "male", l: "Men" }, { v: "non-binary", l: "Non-binary" }];
const CITIES = ["Bengaluru", "Mumbai", "Delhi & NCR"];

export default function Discover() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gender, setGender] = useState("any");
  const [interest, setInterest] = useState("");
  const [city, setCity] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (gender !== "any") params.gender = gender;
      if (interest) params.interest = interest;
      if (city) params.city = city;
      const { data } = await api.get("/hosts", { params });
      setHosts(data.hosts);
    } finally { setLoading(false); }
  }, [gender, interest, city]);

  useEffect(() => { load(); }, [load]);

  const filtered = hosts.filter((h) => h.name.toLowerCase().includes(search.toLowerCase()) || (h.bio || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-sand">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <h1 className="font-display text-4xl md:text-5xl font-black text-charcoal">Discover people</h1>
        <p className="text-charcoal/60 mt-2">Verified adults in Bengaluru ready for a platonic hangout.</p>

        <div className="mt-8 bg-white rounded-2xl border border-border p-5 shadow-sm">
          <div className="relative mb-4">
            <Search className="w-5 h-5 text-charcoal/40 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input data-testid="discover-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or interest..." className="pl-10 rounded-full border-border" />
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-2"><SlidersHorizontal className="w-4 h-4" /> Looking for</div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2">
            {GENDERS.map((g) => (
              <button key={g.v} data-testid={`filter-gender-${g.v}`} onClick={() => setGender(g.v)}
                className={`shrink-0 text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors duration-200 ${gender === g.v ? "bg-terracotta text-white border-terracotta" : "bg-white text-charcoal/70 border-border"}`}>{g.l}</button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-charcoal/50 mb-2 mt-3"><MapPin className="w-4 h-4" /> City</div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            <button onClick={() => setCity("")} className={`shrink-0 text-sm font-semibold px-4 py-1.5 rounded-full border ${!city ? "bg-sage text-white border-sage" : "bg-white text-charcoal/70 border-border"}`}>All cities</button>
            {CITIES.map((c) => (
              <button key={c} data-testid={`filter-city-${c}`} onClick={() => setCity(c)}
                className={`shrink-0 text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors duration-200 ${city === c ? "bg-sage text-white border-sage" : "bg-white text-charcoal/70 border-border"}`}>{c}</button>
            ))}
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 mt-3">
            <button onClick={() => setInterest("")} className={`shrink-0 text-sm font-semibold px-4 py-1.5 rounded-full border ${!interest ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal/70 border-border"}`}>All interests</button>
            {INTERESTS.map((i) => (
              <button key={i} data-testid={`filter-interest-${i}`} onClick={() => setInterest(i)}
                className={`shrink-0 text-sm font-semibold px-4 py-1.5 rounded-full border transition-colors duration-200 ${interest === i ? "bg-charcoal text-white border-charcoal" : "bg-white text-charcoal/70 border-border"}`}>{i}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {[...Array(6)].map((_, i) => <div key={i} className="h-80 bg-white rounded-2xl border border-border animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-charcoal/50 mt-16">No hosts match your filters yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {filtered.map((h, i) => <HostCard key={h.id} host={h} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
